import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DragDrop } from '@uppy/react';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { datadogRum } from '@datadog/browser-rum';
import Uppy, { UppyFile } from '@uppy/core';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import useUppy from 'modules/common/components/UppyUploader/useUppy';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import { ALL_IMAGE_FILE_TYPE, PDF_FILE_TYPE } from 'modules/common/constants/files';
import { Document } from 'modules/common/interfaces/documents';
import UploadingDocumentsScreen from 'modules/common/components/DragAndDrop/UploadingDocumentsScreen';
import Loading from 'modules/HubV2/Bookkeeping/components/Loading';
import { isPdfPasswordProtected } from 'modules/common/components/DragAndDrop/validatePdfPassword';
import PlusIcon from 'modules/common/components/Icons/PlusIcon';
import DocumentTextIcon from 'modules/common/components/Icons/DocumentTextIcon';

export interface DragAndDropRef {
    getFiles: () => UppyFile<any, any>[];
    clearFiles: () => void;
    removeFile: (id: string) => void;
    addFile: (file: File) => void;
    upload: () => void;
    isValidating: () => boolean;
}

/**
 * What this uploader accepts, as member-facing prose, for a caller writing its own idle copy.
 *
 * Not a derivation of the restriction below, which is `image/*` plus PDF: GIF, WebP, SVG, BMP and
 * TIFF all get through unnamed. This is the shortlist a member recognises, not the authority on
 * what gets rejected. `DefaultIdleContent` keeps its own extension list rather than switching to
 * this one, so the two long-standing callers display exactly what they always have.
 */
export const ACCEPTED_FILE_TYPES_LABEL = 'PDF, PNG, JPEG, or HEIC';

/** The generic idle treatment, unchanged for every caller that supplies no `idleContent`. */
function DefaultIdleContent() {
    return (
        <>
            <PlusIcon color="disabled" />

            <Stack direction="column" justifyContent="center" alignItems="center">
                <Typography variant="subtitle1">Click to upload (or drag and drop your file here)</Typography>
                <Typography variant="body2" color="text.secondary">
                    Supported files: .pdf,.jpg,.png,.heic
                </Typography>
            </Stack>
        </>
    );
}

/** The generic in-flight treatment, unchanged for every caller that supplies no `loadingContent`. */
function DefaultLoadingContent() {
    return (
        <>
            <Loading />
            <Typography variant="subtitle1">Uploading file...</Typography>
        </>
    );
}

interface DragAndDropProps {
    documentType: string;
    flowKey: string;
    category: string;
    onUploadSuccess?: (documents?: Document[]) => Promise<any>;
    isSideLoading?: boolean;
    isDisabled?: boolean;
    uploadOnFileAdded?: boolean;
    uploadSource?: string;
    maxNumberOfFiles?: number;
    /** Per-file size cap. Falls back to the shared uploader default when omitted. */
    maxFileSizeBytes?: number;
    onFilesChanged?: (files: UppyFile<any, any>[]) => void;
    onValidatingChange?: (isValidating: boolean) => void;
    /**
     * Whether a batch is between the member and us, for a caller that has to gate on it.
     *
     * Not the same question as `onFilesChanged` returning an empty queue: this stays true from the
     * first file added until `onUploadSuccess` has resolved, covering the window where the uploader
     * has let go of the files but the caller's view of them has not caught up. A gate built on the
     * queue alone reopens inside that gap.
     */
    onUploadingChange?: (isUploading: boolean) => void;
    fileActionLabel?: string;
    /**
     * Everything inside the drop zone when it is idle — glyph, heading, whatever else the surface
     * wants to say. Centred and gap-spaced by the container; the content itself is the caller's.
     *
     * A slot rather than flat `title`/`subtitle`/`hint` props, which would fix the message at three
     * lines and force this component wider for every surface wanting a fourth element or a link.
     * The cost is that each caller picks its own `Typography` variants, so two dropzones can drift.
     */
    idleContent?: React.ReactNode;
    /**
     * Everything the in-flight overlay shows, replacing the default spinner and caption entirely.
     * A node, not a string, so content that reports real progress — a count, per-file bars, its own
     * animation — needs nothing added here.
     */
    loadingContent?: React.ReactNode;
    /**
     * Accessible name for the in-flight overlay. Pass it whenever `loadingContent` says something
     * more specific than the default, or the announced and visible text disagree.
     */
    loadingLabel?: string;
}

const DragAndDrop = forwardRef<DragAndDropRef, Readonly<DragAndDropProps>>(function DragAndDrop(
    {
        documentType,
        flowKey,
        category,
        onUploadSuccess = undefined,
        isSideLoading = false,
        isDisabled = false,
        uploadOnFileAdded = true,
        uploadSource = '',
        maxNumberOfFiles = 1,
        maxFileSizeBytes = undefined,
        onFilesChanged,
        onValidatingChange,
        onUploadingChange,
        fileActionLabel = 'Remove',
        idleContent = <DefaultIdleContent />,
        loadingContent = <DefaultLoadingContent />,
        loadingLabel = undefined,
    },
    ref
) {
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [addedFiles, setAddedFiles] = useState<UppyFile<any, any>[]>([]);
    const { enqueueSnackbar } = useSnackbar();
    const { sendTrackingEvent } = useEventTracking();

    useEffect(() => {
        onValidatingChange?.(isValidating);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValidating]);

    useEffect(() => {
        onUploadingChange?.(isUploading);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUploading]);

    /**
     * A batch is finished when nothing is still in flight. Failures count as settled — they
     * are not coming back — so a drop where one file fails still hands back the ones that
     * landed instead of stranding them. Takes the instance as an argument because it is
     * declared above `useUppy`, and both handlers below already hold it at call time.
     */
    const settleBatch = (instance: Uppy) => {
        const files = instance.getFiles();
        if (files.some((file) => !file.progress?.uploadComplete && !file.error)) {
            return;
        }

        const uploaded = files
            .filter((file) => file.progress?.uploadComplete)
            .map<Document>((file) => ({ ...file.response?.body, source: 'local' } as Document));

        instance.removeFiles(files.map((file) => file.id));
        setAddedFiles([]);
        onFilesChanged?.([]);

        if (uploaded.length > 0 && onUploadSuccess) {
            onUploadSuccess(uploaded).finally(() => setIsUploading(false));
            return;
        }
        setIsUploading(false);
    };

    // Stable identity, because `useUppy` keys an effect on it and `uppy.setMeta` shallow-clones
    // every file in state on each call. A fresh literal here re-ran that on every render, which
    // this component now does far more often than it used to.
    const meta = useMemo(
        () => ({ document_type: documentType, upload_source: uploadSource }),
        [documentType, uploadSource]
    );

    const { uppy } = useUppy({
        documentType,
        meta,
        options: {
            restrictions: {
                allowedFileTypes: [ALL_IMAGE_FILE_TYPE, PDF_FILE_TYPE],
                maxNumberOfFiles,
                ...(maxFileSizeBytes != null ? { maxFileSize: maxFileSizeBytes } : {}),
            },
        },
        shouldPreloadData: false,
        disabled: isUploading || isValidating || isDisabled,
        onFileAdded: async (file: UppyFile<any, any>) => {
            setAddedFiles((prev) => {
                const next = [...prev, file];
                onFilesChanged?.(next);
                return next;
            });

            if (file.type === PDF_FILE_TYPE && file.data instanceof Blob) {
                setIsValidating(true);
                try {
                    if (await isPdfPasswordProtected(file.data)) {
                        uppy.removeFile(file.id);
                        setAddedFiles((prev) => {
                            const next = prev.filter((f) => f.id !== file.id);
                            onFilesChanged?.(next);
                            return next;
                        });
                        enqueueSnackbar('This PDF is password protected. Please remove the password and re-upload.', {
                            variant: 'error',
                        });
                        setIsValidating(false);
                        return;
                    }
                } catch (err) {
                    sendDatadogRumError('PDF password validation failed', err as Error, {
                        type: 'PDF_VALIDATION_ERROR',
                        flowKey,
                        documentType,
                    });
                }
                setIsValidating(false);
            }

            if (uploadOnFileAdded) {
                setIsUploading(true);
                uppy.upload();
            }
        },
        // eslint-disable-next-line
        // @ts-ignore
        onFileRemoved: (file: UppyFile<any, any>) => {
            setAddedFiles((prev) => {
                const next = prev.filter((f) => f.id !== file.id);
                onFilesChanged?.(next);
                return next;
            });
        },
        onUploadSuccess: () => {
            sendTrackingEvent(`${flowKey} Document Uploaded`, {
                action: 'uploaded',
                category,
                documentType,
            });
            datadogRum.addAction(`${flowKey} Document Uploaded`, {
                type: 'DOCUMENT_UPLOADED',
                documentType,
            });
            settleBatch(uppy);
        },
        onUploadError: (err: Error) => {
            sendDatadogRumError('Document upload error', err, {
                type: 'DOCUMENT_UPLOAD_ERROR',
                flowKey,
                documentType,
            });
            enqueueSnackbar('Error uploading file', { variant: 'error' });
            if (uploadOnFileAdded) {
                // Settle rather than clear. Removing every file here aborts the ones still
                // uploading, so a single failure part-way through a batch took the rest of
                // it down. Settling also covers the case where the failure is the last
                // event, which would otherwise strand the files that succeeded.
                settleBatch(uppy);
                return;
            }
            setIsUploading(false);
        },
    });

    useImperativeHandle(ref, () => ({
        getFiles: () => uppy.getFiles(),
        clearFiles: () => {
            const files = uppy.getFiles();
            uppy.removeFiles(files.map((f) => f.id));
            setAddedFiles([]);
        },
        removeFile: (id: string) => uppy.removeFile(id),
        addFile: (file: File) => {
            uppy.addFile({
                name: file.name,
                type: file.type,
                data: file,
            });
        },
        upload: () => {
            setIsUploading(true);
            uppy.upload();
        },
        isValidating: () => isValidating,
    }));

    const shouldHideDropZone = !uploadOnFileAdded && addedFiles.length >= maxNumberOfFiles;

    return (
        <>
            {!uploadOnFileAdded && addedFiles.length > 0 && (
                <Stack direction="column" gap={1} width="100%">
                    <Typography variant="subtitle1" color="text.secondary">
                        {addedFiles.length} file{addedFiles.length > 1 ? 's' : ''} added
                    </Typography>
                    {addedFiles.map((file) => (
                        <Paper
                            key={file.id}
                            variant="filled"
                            sx={{
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexDirection: 'row',
                            }}>
                            <Stack direction="row" gap={2} alignItems="center">
                                <DocumentTextIcon />
                                <Typography variant="subtitle1">{file.name}</Typography>
                            </Stack>
                            <Button
                                onClick={() => uppy.removeFile(file.id)}
                                variant="outlined"
                                color="grey"
                                sx={{ color: 'text.primary' }}>
                                {fileActionLabel}
                            </Button>
                        </Paper>
                    ))}
                </Stack>
            )}
            {!shouldHideDropZone && (
                <Paper
                    aria-label="Drag and drop area"
                    variant="filled"
                    onDragEnterCapture={() => {
                        setIsDragOver(true);
                    }}
                    onDragLeaveCapture={() => {
                        setIsDragOver(false);
                    }}
                    sx={{
                        height: {
                            xs: 'auto',
                            md: '100%',
                        },
                        width: '100%',
                        maxWidth: '100%',
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr)',
                        gridTemplateAreas: '"content"',
                        alignItems: 'center',
                        position: 'relative',
                        transition: 'background-color 0.2s ease-in-out',
                        backgroundColor: 'cement.100',
                        '&:hover': {
                            backgroundColor: 'cement.50',
                        },
                        ...(isDragOver && {
                            backgroundColor: 'cement.50',
                        }),
                        '& .uppy-Container, & .uppy-Root, & .uppy-DragDrop-container': {
                            gridArea: 'content',
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'transparent',
                            borderColor: 'cement.300',
                            '&:hover': {
                                borderColor: 'cement.400',
                            },
                            ...(isDragOver && {
                                borderColor: 'cement.400',
                            }),
                            zIndex: 1,
                            '& .uppy-DragDrop-inner': {
                                visibility: 'hidden',
                            },
                        },
                    }}>
                    <DragDrop uppy={uppy} />
                    {(isUploading || isValidating || isSideLoading) && (
                        <UploadingDocumentsScreen content={loadingContent} label={loadingLabel} />
                    )}
                    {!isUploading && !isValidating && !isSideLoading && (
                        <Stack
                            aria-label="Drag and drop area"
                            gap={2}
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                gridArea: 'content',
                            }}>
                            {idleContent}
                        </Stack>
                    )}
                </Paper>
            )}
        </>
    );
});

export default DragAndDrop;
